// src/lib/leadCache.js

const DB_NAME = "expolead_local_db";
const DB_VERSION = 2;

let dbInstance = null;

function getStoreName(userId) {
  return `leads_${String(userId || "guest").replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

function openDB(userId) {
  if (dbInstance && dbInstance.objectStoreNames.contains(getStoreName(userId))) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      return reject(new Error("IndexedDB is not supported in this environment"));
    }

    const storeName = getStoreName(userId);
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        const store = db.createObjectStore(storeName, { keyPath: "id" });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      dbInstance.onversionchange = () => {
        dbInstance.close();
        dbInstance = null;
      };
      dbInstance.onclose = () => {
        dbInstance = null;
      };
      resolve(dbInstance);
    };

    request.onerror = (event) => reject(event.target.error);
  });
}

function cleanLeadForCache(rawLead) {
  if (!rawLead) return null;
  const id = rawLead.id || rawLead._id;
  if (!id) return null;

  // Never store heavy base64 strings in list cache to prevent RAM exhaustion
  const { image_base64, back_image_base64, ...clean } = rawLead;
  return { ...clean, id: String(id) };
}

export async function getLocalLeads(userId) {
  if (!userId) return [];
  try {
    const db = await openDB(userId);
    const storeName = getStoreName(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const req = store.getAll();

      req.onsuccess = () => {
        const results = req.result || [];
        results.sort((a, b) => {
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return timeB - timeA;
        });
        resolve(results);
      };

      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn("IndexedDB read error:", err);
    return [];
  }
}

export async function saveLocalLead(userId, lead) {
  if (!userId) return;
  const clean = cleanLeadForCache(lead);
  if (!clean) return;

  try {
    const db = await openDB(userId);
    const storeName = getStoreName(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.put(clean);

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB write error:", err);
  }
}

export async function deleteLocalLead(userId, id) {
  if (!userId || !id) return;
  try {
    const db = await openDB(userId);
    const storeName = getStoreName(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.delete(String(id));

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB delete error:", err);
  }
}

export async function syncLocalLeads(userId, serverLeads) {
  if (!userId || !Array.isArray(serverLeads) || serverLeads.length === 0) return;
  try {
    const db = await openDB(userId);
    const storeName = getStoreName(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);

      for (const item of serverLeads) {
        const clean = cleanLeadForCache(item);
        if (clean) {
          store.put(clean);
        }
      }

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB sync error:", err);
  }
}

export async function clearLocalLeads(userId) {
  if (!userId) return;
  try {
    const db = await openDB(userId);
    const storeName = getStoreName(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.clear();

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("IndexedDB clear error:", err);
  }
}